import { useSuspenseQuery } from '@apollo/client';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Content,
  Header,
  Heading,
  Separator,
  SidebarTrigger,
  Stack,
} from '@eluve/components';
import { LlmOutputTypesLookup } from '@eluve/graphql-types';
import { dynamicArtifactTemplateSchema } from '@eluve/llm-outputs';
import {
  DynamicArtifactTemplateBuilder,
  templateBuilderStore,
} from '@eluve/smart-blocks';

import { DynamicArtifactTemplatePreview } from './components/DynamicArtifactPreview';
import { PrefillTemplateSection } from './components/PrefillTemplateSection';
import { TemplateEditingPageHeaderButtons } from './components/TemplateEditingPageHeaderButtons';
import { getOutputTemplateQuery } from './operations';
import { useCreateTemplate } from './useCreateTemplate';
import { useIsOrganizationRoute } from './useIsOrganizationRoute';

export const CloneOutputTemplatePage: FC = () => {
  const { outputTemplateId } = useParams() as { outputTemplateId: string };
  const navigate = useNavigate();
  const isOrganizationRoute = useIsOrganizationRoute();

  const { handleSubmitTemplate, handleSaveDraftTemplate } = useCreateTemplate();

  const [isValid, setIsValid] = useState(false);

  const handleIsValidChange = useCallback((valid: boolean) => {
    setIsValid(valid);
  }, []);

  const {
    data: { llmOutputTemplatesByPk: outputTemplate },
  } = useSuspenseQuery(getOutputTemplateQuery, {
    variables: {
      id: outputTemplateId,
    },
  });

  const artifactTemplate = useMemo(() => {
    const parsed = dynamicArtifactTemplateSchema.safeParse(
      outputTemplate?.variants?.[0]?.template,
    );
    if (!parsed.success) {
      return undefined;
    }

    return parsed.data;
  }, [outputTemplate?.variants]);

  useEffect(() => {
    if (artifactTemplate) {
      templateBuilderStore.send({
        type: 'hydrateArtifact',
        artifactTemplate,
        outputType:
          outputTemplate?.llmOutputType ?? LlmOutputTypesLookup.SOAP_NOTE,
      });
    }
  }, [artifactTemplate, outputTemplate?.llmOutputType]);

  return (
    <>
      <Header>
        {isOrganizationRoute && <SidebarTrigger />}
        <div className="my-2 w-full py-2">
          <Heading>Create Template</Heading>
        </div>

        <TemplateEditingPageHeaderButtons
          submitDisabled={!isValid}
          saveDraftDisabled={!isValid}
          handleSaveDraft={handleSaveDraftTemplate}
          handleSubmit={handleSubmitTemplate}
          handleDeleteDraft={() => navigate('..')}
        />
      </Header>
      <Content padded>
        <Stack
          align="stretch"
          className="divide-x"
          gap={2}
          direction="horizontal"
          fullHeight
        >
          <Stack
            className="min-w-0 flex-1 overflow-y-auto p-6"
            gap={6}
            fullHeight
          >
            <PrefillTemplateSection />
            <Separator />
            <DynamicArtifactTemplateBuilder
              onIsValidChange={handleIsValidChange}
              showJsonSchema
            />
          </Stack>
          <Stack
            className="min-w-0 flex-1 overflow-y-auto bg-bg-tertiary p-6"
            gap={2}
            fullHeight
          >
            <DynamicArtifactTemplatePreview />
          </Stack>
        </Stack>
      </Content>
    </>
  );
};
